import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { format } from "date-fns";
import { addLocale } from 'primereact/api';

import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { InputSwitch } from 'primereact/inputswitch';
import { Tag } from 'primereact/tag';
import { Calendar } from 'primereact/calendar';
import { FileUpload } from 'primereact/fileupload';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { useAuth } from '../../context/AuthContext';

import { PersonaEntity } from '../../entity/PersonaEntity';
import PageHeader from '../../components/ui/PageHeader';
import DialogHeader from '../../components/ui/DialogHeader';
import ActionToolbar from '../../components/ui/ActionToolbar';
import '../../css/Formulario.css';

addLocale('es', {
    firstDayOfWeek: 1,
    dayNames: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
    dayNamesShort: ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'],
    dayNamesMin: ['D', 'L', 'M', 'X', 'J', 'V', 'S'],
    monthNames: ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'],
    monthNamesShort: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
    today: 'Hoy',
    clear: 'Limpiar',
});

export const Personas = () => {
    const { axiosInstance } = useAuth();

    const colors = {
        primary: '#0B396E',
        accent: '#800000',
        text: '#444444',
    };

    // Estados
    const empty = PersonaEntity;
    const [data, setData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [EntidadNewDialog, setEntidadNewDialog] = useState(false);
    const [bulkUploadDialog, setBulkUploadDialog] = useState(false);
    const [product, setProduct] = useState(empty);
    const [submitted, setSubmitted] = useState(false);
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isSearchingRENIEC, setIsSearchingRENIEC] = useState(false);

    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 0
    });

    const baseUrl = 'persona/';

    const toast = useRef(null);
    const dt = useRef(null);

    // Helpers de Formato
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = (dateString instanceof Date) ? dateString : new Date(dateString + 'T00:00:00');
            return format(date, 'dd/MM/yyyy');
        } catch (e) { return '-'; }
    };

    const stringToDate = (dateString) => {
        if (!dateString) return null;
        if (dateString instanceof Date) return dateString;
        return new Date(dateString + 'T00:00:00');
    };

    // Peticiones API
    const peticionGet = useCallback(async () => {
        setLoading(true);
        try {
            const page = lazyParams.first / lazyParams.rows;
            const response = await axiosInstance.get('/persona/', {
                params: {
                    page,
                    size: lazyParams.rows,
                    search: globalFilterValue || ''
                }
            });
            setData(response.data.content);
            setTotalRecords(response.data.totalElements);
        } catch (error) {
            console.error(error);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los datos.', life: 5000 });
        } finally {
            setLoading(false);
        }
    }, [axiosInstance, globalFilterValue, lazyParams]);

    const peticionRENIEC = useCallback(async () => {
        if (!product.docIdentidad) {
            toast.current.show({ severity: 'warn', summary: 'Advertencia', detail: 'Ingrese un DNI para buscar.', life: 3000 });
            return;
        }

        setIsSearchingRENIEC(true);

        try {
            const response = await axios.get("https://dniruc.apisperu.com/api/v1/dni/" + product.docIdentidad + "?token=eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJlbWFpbCI6ImpnYWl0YW5yQHBqLmdvYi5wZSJ9.-nX87AiyjDvfW2SeGAhWFnx0MDCiB8meK06aAAlVfJQ");
            const { nombres, apellidoPaterno, apellidoMaterno } = response.data;
            setProduct((prevProduct) => ({
                ...prevProduct,
                nombre: nombres,
                apellido: `${apellidoPaterno} ${apellidoMaterno}`,
            }));
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Datos obtenidos de RENIEC.', life: 3000 });
        } catch (error) {
            console.error("Error al consultar RENIEC:", error);
            const detail = error.response?.status === 404 ? 'DNI no encontrado o error en la consulta.' : 'No se pudieron obtener datos de RENIEC.';
            toast.current.show({ severity: 'error', summary: 'Error', detail: detail, life: 5000 });
        } finally {
            setIsSearchingRENIEC(false);
        }
    }, [product.docIdentidad, toast]);

    const peticionPost = async () => {
        setIsSaving(true);
        try {
            const body = { ...product };
            delete body.id;
            if (body.fecNacimiento instanceof Date) body.fecNacimiento = format(body.fecNacimiento, 'yyyy-MM-dd');

            await axiosInstance.post('/persona/', body);
            peticionGet();
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Persona registrada.', life: 3000 });
            setEntidadNewDialog(false);
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Error al registrar.', life: 5000 });
        } finally {
            setIsSaving(false);
        }
    };

    const peticionPut = async () => {
        setIsSaving(true);
        try {
            const body = { ...product };
            if (body.fecNacimiento instanceof Date) body.fecNacimiento = format(body.fecNacimiento, 'yyyy-MM-dd');

            await axiosInstance.put('/persona/', body);
            peticionGet();
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Persona actualizada.', life: 3000 });
            setEntidadNewDialog(false);
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Error al modificar.', life: 5000 });
        } finally {
            setIsSaving(false);
        }
    };

    useEffect(() => {
        peticionGet();
    }, [lazyParams, globalFilterValue, peticionGet]);

    // Handlers
    const openNew = () => {
        setProduct(empty);
        setSubmitted(false);
        setEntidadNewDialog(true);
    };

    const editProduct = (rowData) => {
        setProduct({
            ...rowData,
            fecNacimiento: rowData.fecNacimiento ? stringToDate(rowData.fecNacimiento) : null
        });
        setEntidadNewDialog(true);
    };

    const saveProduct = () => {
        setSubmitted(true);
        if (product.docIdentidad && product.nombre && product.apellido) {
            product.id ? peticionPut() : peticionPost();
        } else {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Complete los campos obligatorios.', life: 3000 });
        }
    };

    const onInputChange = (e, name) => {
        const val = (e.target && e.target.value) || e.value;
        setProduct(prev => ({ ...prev, [name]: val }));
    };

    const hideDialogNew = () => setEntidadNewDialog(false);

    const personaBody = (rowData) => (
        <span>{`${rowData.nombre} ${rowData.apellido}`} <br />
            <small className="text-muted">{rowData.docIdentidad}</small></span>
    );

    const actionBodyTemplate = (rowData) => (
        <Button icon="pi pi-pencil" className="p-button-rounded p-button-text"
            style={{ color: colors.accent }}
            onClick={() => editProduct(rowData)} />
    );

    const handleDownloadTemplate = () => {
        const link = document.createElement('a');
        link.href = '/formato/Formato_Personas.xlsx';
        link.download = 'plantilla-personas.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleBulkUpload = async (event) => {
        const file = event.files[0];
        const formData = new FormData();
        formData.append('file', file);
        const endpoint = `${baseUrl}cargar-masiva`;

        try {
            const response = await axiosInstance.post(endpoint, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            const cargaMasivaResponse = response.data;
            const { totalRegistros, registrosNuevos, registrosActualizados, registrosFallidos, errores } = cargaMasivaResponse;

            const summaryDetail = `Total: ${totalRegistros}. Nuevos: ${registrosNuevos}. Actualizados: ${registrosActualizados}. Fallidos: ${registrosFallidos}.`;

            if (registrosFallidos === 0) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Carga Masiva Exitosa',
                    detail: summaryDetail,
                    life: 5000
                });
            } else if (registrosNuevos + registrosActualizados > 0) {
                toast.current.show({
                    severity: 'warn',
                    summary: 'Carga Parcial con Errores',
                    detail: `${summaryDetail} Exportando lista de errores...`,
                    life: 10000
                });
                exportarErroresAExcel(errores);

            } else if (totalRegistros > 0 && registrosFallidos === totalRegistros) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Fallo Total de Carga',
                    detail: `${summaryDetail} Se generó un archivo de errores.`,
                    life: 10000
                });
                exportarErroresAExcel(errores);

            } else {
                toast.current.show({ severity: 'info', summary: 'Proceso Finalizado', detail: 'El archivo estaba vacío o no se detectaron registros válidos.', life: 5000 });
            }

            setBulkUploadDialog(false);
            peticionGet();

        } catch (error) {
            const errorData = error.response?.data;
            let errorMessage = 'Error desconocido al procesar el archivo o de conexión.';
            if (errorData && errorData.errores && errorData.errores.length > 0) {
                errorMessage = `Error fatal de archivo: ${errorData.errores[0]}`;
            } else {
                errorMessage = error.response?.data?.message || error.message || 'Error de conexión o servidor.';
            }
            toast.current.show({ severity: 'error', summary: 'Error de Carga', detail: errorMessage, life: 5000 });
        }
    };

    const exportarErroresAExcel = (errores) => {
        if (!errores || errores.length === 0) return;

        const datosParaExportar = errores.map((errorString, index) => ({
            '#': index + 1,
            'Detalle del Error (Fila, DNI y Causa Incluidos)': errorString,
        }));

        const ws = XLSX.utils.json_to_sheet(datosParaExportar);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Errores de Carga");
        // Generar un nombre de archivo único con fecha y hora
        const today = format(new Date(), 'yyyyMMdd_HHmmss');
        const filename = `Errores_Personas_CargaMasiva_${today}.xlsx`;

        try {
            XLSX.writeFile(wb, filename);
            toast.current.show({ severity: 'info', summary: 'Exportación Completa', detail: 'El archivo de errores se ha descargado.', life: 3000 });
        } catch (e) {
            toast.current.show({ severity: 'error', summary: 'Error de Exportación', detail: 'No se pudo generar el archivo Excel de errores.', life: 3000 });
            console.error("Error al escribir el archivo XLSX:", e);
        }
    };

    const exportCSV = () => dt.current.exportCSV();

    const BuscarDNI = () => {
        setSubmitted(true);
        if (!isSearchingRENIEC && product.docIdentidad && product.docIdentidad.trim()) {
            peticionRENIEC();
        } else if (!product.docIdentidad || !product.docIdentidad.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Advertencia', detail: 'Ingrese un DNI válido para buscar.', life: 3000 });
        }
    };

    return (
        <div className="usuario-page"
            style={{
                background: 'rgba(255, 255, 255, 0.3)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
            <div className="container">
                <PageHeader
                    title="Gestión de Personas"
                    description="Administra los datos personales y de contacto del personal"
                    icon="pi pi-users"
                />

                <div className="main-content">
                    <div className="content-card">
                        <ConfirmDialog />
                        <Toast ref={toast} />

                        <ActionToolbar
                            onNew={openNew}
                            newLabel="Nueva Persona"
                            onSearch={(val) => setGlobalFilterValue(val)}
                            searchValue={globalFilterValue}
                            searchPlaceholder="Buscar por DNI o Apellidos..."
                            extraActions={
                                <>
                                    <Button
                                        tooltip="Carga Masiva"
                                        icon="pi pi-upload"
                                        style={{ backgroundColor: 'white', borderColor: colors.primary, color: colors.primary }}
                                        onClick={() => setBulkUploadDialog(true)}
                                        className="mr-2"
                                    />
                                    <Button
                                        icon="pi pi-download"
                                        tooltip="Exportar CSV"
                                        className="btn-export"
                                        onClick={exportCSV}
                                    />
                                </>
                            }
                        />

                        <DataTable
                            ref={dt}
                            value={data}
                            lazy
                            dataKey="id"
                            paginator
                            first={lazyParams.first}
                            rows={lazyParams.rows}
                            totalRecords={totalRecords}
                            onPage={(e) => setLazyParams(e)}
                            loading={loading}
                            size='small'
                            className="datatable-responsive"
                            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                            currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} Personas"
                            emptyMessage="No existen registros.">

                            <Column field="escalafon" header="Escalafon" />
                            <Column header="Persona" body={personaBody} />
                            <Column field="email" header="Correo" />
                            <Column field="fecNacimiento" header="Fec. Nacimiento" body={(r) => formatDate(r.fecNacimiento)} />
                            <Column field="estado" header="Estado" body={(r) => <Tag value={r.estado ? 'Activo' : 'Inactivo'} severity={r.estado ? 'success' : 'warning'} />} />
                            <Column body={actionBodyTemplate} />
                        </DataTable>

                        {/* Dialogo de Edición/Registro */}
                        <Dialog
                            visible={EntidadNewDialog}
                            style={{ width: '750px', maxWidth: '95vw' }}
                            header={
                                <DialogHeader
                                    title={<span style={{ color: 'white' }}>{product.id ? 'Editar Persona' : 'Nueva Persona'}</span>}
                                    subtitle={product.id ? 'Actualice la información personal' : 'Registre una nueva persona en el sistema'}
                                    icon="pi pi-user-plus"
                                />
                            }
                            modal
                            className="p-fluid custom-profile-dialog"
                            footer={
                                <div className="dialog-footer-buttons">
                                    <Button label="Cancelar" icon="pi pi-times" className="p-button-text p-button-secondary" onClick={hideDialogNew} />
                                    <Button label="Guardar" icon="pi pi-check" onClick={saveProduct} autoFocus />
                                </div>
                            }
                            onHide={hideDialogNew}
                        >

                            <div className="formgrid grid">
                                <div className="field col-12 md:col-6">
                                    <label>Estado</label>
                                    <InputSwitch checked={product.estado} onChange={(e) => onInputChange(e, 'estado')} />
                                </div>
                            </div>

                            <div className="formgrid grid">
                                <div className="field col-12 md:col-6">
                                    <label htmlFor="docIdentidad">DNI</label>
                                    <div className="p-inputgroup">
                                        <InputText id="docIdentidad" value={product.docIdentidad} onChange={(e) => onInputChange(e, 'docIdentidad')} maxLength={8} />
                                        <Button
                                            icon={isSearchingRENIEC ? "pi pi-spin pi-spinner" : "pi pi-search"}
                                            onClick={BuscarDNI}
                                            disabled={isSearchingRENIEC || isSaving || !product.docIdentidad || product.docIdentidad.length !== 8}
                                            className="search-dni-btn"
                                        />
                                    </div>
                                </div>
                                <div className="field col-12 md:col-6">
                                    <label htmlFor="escalafon">Escalafón</label>
                                    <InputText id="escalafon" value={product.escalafon} onChange={(e) => onInputChange(e, 'escalafon')} />
                                </div>
                            </div>

                            <div className="formgrid grid">
                                <div className="field col-12 md:col-6">
                                    <label htmlFor="nombre">Nombres</label>
                                    <InputText id="nombre" value={product.nombre} onChange={(e) => onInputChange(e, 'nombre')} required />
                                </div>
                                <div className="field col-12 md:col-6">
                                    <label htmlFor="apellido">Apellidos</label>
                                    <InputText id="apellido" value={product.apellido} onChange={(e) => onInputChange(e, 'apellido')} required />
                                </div>
                            </div>

                            <div className="formgrid grid">
                                <div className="field col-12 md:col-6">
                                    <label>Fecha de Nacimiento</label>
                                    <Calendar
                                        id="fecNacimiento"
                                        name="fecNacimiento"
                                        placeholder="dd/mm/aaaa"
                                        value={product.fecNacimiento ? stringToDate(product.fecNacimiento) : null}
                                        dateFormat="dd/mm/yy"
                                        locale='es'
                                        onChange={(e) => onInputChange(e, 'fecNacimiento')}
                                        disabled={isSaving}
                                    />
                                </div>
                                <div className="field col-12 md:col-6">
                                    <label>Teléfono</label>
                                    <InputText value={product.nroTelefono} onChange={(e) => onInputChange(e, 'nroTelefono')} />
                                </div>
                            </div>

                            <div className="field">
                                <label>Dirección</label>
                                <InputText value={product.direccion} onChange={(e) => onInputChange(e, 'direccion')} />
                            </div>
                        </Dialog>

                        {/* DIÁLOGO DE CARGA MASIVA - Se mantiene igual, pero se puede añadir un spinner para la carga */}
                        <Dialog visible={bulkUploadDialog} style={{ width: '52vw' }} header="Carga Masiva de Personas" modal onHide={() => setBulkUploadDialog(false)}>
                            <div className="p-fluid grid formgrid">
                                <div className="col-12">
                                    <p className="text-sm text-600 mb-2">Utilice esta herramienta para registrar/actualizar múltiples personas de forma eficiente.</p>
                                </div>
                                <div className="col-12 md:col-6 flex flex-column justify-content-between p-4 border-right-1 surface-border h-full">
                                    <p className="font-bold">1. Descargar la plantilla</p>
                                    <p className="text-sm">Para evitar errores, use nuestra plantilla oficial de Excel con el formato correcto. Las columnas esperadas son: **docIdentidad, escalafon, nombre, apellido, fecNacimiento, direccion, email, nroTelefono**.</p>
                                    <Button
                                        label="Descargar Plantilla"
                                        icon="pi pi-download"
                                        className="p-button-outlined p-button-secondary mt-auto"
                                        onClick={handleDownloadTemplate} />
                                </div>
                                <div className="col-12 md:col-6 flex flex-column justify-content-between p-4">
                                    <p className="font-bold">2. Subir el archivo</p>
                                    <p className="text-sm">Seleccione o arrastre aquí el archivo .xlsx con la plantilla llena.</p>
                                    <FileUpload
                                        name="file"
                                        //url={`${baseUrl}/cargar-masiva`}
                                        accept=".xlsx"
                                        maxFileSize={20000000}
                                        customUpload
                                        uploadHandler={handleBulkUpload}
                                        chooseLabel="Elegir Archivo"
                                        chooseOptions={{
                                            icon: 'pi pi-fw pi-file-excel',
                                            label: 'Elegir Archivo',
                                            style: {
                                                backgroundColor: colors.primary,
                                                borderColor: colors.primary,
                                                color: 'white'
                                            },
                                            className: 'p-button-sm p-button-raised'
                                        }}
                                        uploadLabel="Cargar Datos"
                                        uploadOptions={{
                                            icon: 'pi pi-fw pi-upload',
                                            label: 'Cargar Datos',
                                            style: {
                                                backgroundColor: colors.accent,
                                                borderColor: colors.accent,
                                                color: 'white'
                                            },
                                            className: 'p-button-sm p-button-raised'
                                        }}
                                        cancelLabel="Eliminar"
                                        cancelOptions={{
                                            icon: 'pi pi-fw pi-times',
                                            label: 'Eliminar',
                                            style: {
                                                backgroundColor: 'transparent',
                                                borderColor: colors.accent,
                                                color: colors.accent
                                            },
                                            className: 'p-button-sm p-button-text'
                                        }}
                                        className="p-button-sm p-button-raised"
                                        emptyTemplate={
                                            <div className="flex flex-column align-items-center justify-content-center p-3 border-2 border-dashed border-round surface-border">
                                                <i className="pi pi-file-excel text-5xl text-400 mb-3" />
                                                <p className="m-0 text-700">Arrastra y suelta tu archivo aquí</p>
                                                <p className="m-0 text-500 text-sm">O haz clic para explorar</p>
                                            </div>
                                        }
                                    />
                                </div>
                            </div>
                        </Dialog>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default Personas;